/**
 * Manual Adjustment Card for the Activity Log Tab.
 *
 * Renders a single manual adjustment entry with undo/redo controls.
 * This variant accepts an optional edgeFieldMap to resolve opaque
 * edge UUIDs into human-readable "source -> target" labels.
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import type { CanvasManualAdjustmentModel } from '../../services/api';
import { toRecord } from '../../utils/dataUtils';

interface ManualAdjustmentCardProps {
  adjustment: CanvasManualAdjustmentModel;
  edgeFieldMap?: Record<string, string>;
  onUndo?: (adjustmentId: string) => Promise<void>;
  onRedo?: (adjustmentId: string) => Promise<void>;
  undoingId?: string | null;
  redoingId?: string | null;
}

const ManualAdjustmentCard: React.FC<ManualAdjustmentCardProps> = ({
  adjustment,
  edgeFieldMap,
  onUndo,
  onRedo,
  undoingId,
  redoingId,
}) => {
  const detailsRecord = toRecord(adjustment.details);
  const audit = toRecord(detailsRecord.audit);
  const isUndone = Boolean(audit.is_undone);
  const hasUndoState = Boolean(audit.previous_state);
  const hasRedoState = Boolean(audit.resulting_state);
  const undoDisabled = !onUndo || !adjustment.reversible || !hasUndoState || isUndone || undoingId === adjustment.adjustment_id;
  const redoDisabled = !onRedo || !adjustment.reversible || !hasRedoState || !isUndone || redoingId === adjustment.adjustment_id;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderColor: isUndone ? 'warning.light' : 'divider',
        backgroundColor: isUndone ? 'warning.50' : 'background.paper',
      }}
    >
      <Stack spacing={0.75}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              label={String(adjustment.status ?? '')}
              color={adjustment.status === 'approved' ? 'success' : 'warning'}
              variant="outlined"
            />
            <Typography variant="subtitle2">
              {String(adjustment.target_kind ?? '').toUpperCase()} &bull; {edgeFieldMap?.[adjustment.target_id] || String(adjustment.target_id ?? '')}
            </Typography>
          </Stack>
          {(onUndo || onRedo) && adjustment.reversible && (
            <Stack direction="row" spacing={1}>
              {onUndo && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={undoingId === adjustment.adjustment_id ? <CircularProgress size={14} /> : <UndoIcon fontSize="small" />}
                  onClick={(e) => { e.stopPropagation(); onUndo(adjustment.adjustment_id); }}
                  disabled={undoDisabled}
                >
                  Undo
                </Button>
              )}
              {onRedo && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={redoingId === adjustment.adjustment_id ? <CircularProgress size={14} /> : <RedoIcon fontSize="small" />}
                  onClick={(e) => { e.stopPropagation(); onRedo(adjustment.adjustment_id); }}
                  disabled={redoDisabled}
                >
                  Redo
                </Button>
              )}
            </Stack>
          )}
        </Stack>
        {adjustment.rationale && typeof adjustment.rationale === 'string' && (
          <Typography variant="body2" color="text.secondary">
            {adjustment.rationale}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          {String(adjustment.author ?? 'unknown')} &bull; {new Date(adjustment.created_at).toLocaleString()}
        </Typography>
      </Stack>
    </Paper>
  );
};

export default ManualAdjustmentCard;
